import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { MobileFormPage } from '@/components/MobileFormPage';
import { useToast } from '@/components/toastStore';
import { useCamadaActiva } from '@/lib/queries/camadas';
import { useTiposPienso } from '@/lib/queries/catalogos';
import {
  useCreateEntregaPienso,
  useEntregasDeCamada,
  useUpdateEntregaPienso,
} from '@/lib/queries/entregasPienso';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { Decimal } from '@/lib/offline/decimal';
import { decimalSolicitud, entradaDecimal, formatoDecimal } from '@/lib/liquidacion';
import { extractApiMessage } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

type Draft = { fecha: string; kg: string; tipo: string; albaran: string; observaciones: string };
function canonical(values: Draft) {
  let kg = values.kg.trim();
  try {
    kg = new Decimal(decimalSolicitud(kg)).toString();
  } catch {
    /* Keep invalid input dirty. */
  }
  return {
    ...values,
    kg,
    albaran: values.albaran.trim(),
    observaciones: values.observaciones.trim(),
  };
}
export function CargaEntregaPiensoPage() {
  const { camadaId: parameter } = useParams();
  const [search] = useSearchParams();
  const entryParameter = search.get('id');
  const user = useAuthStore((s) => s.user);
  const owner = user?.id ?? null;
  useEntityMappings();
  const farm = currentBundle()?.explotacion_id ?? null;
  const identity = owner + ':' + user?.rol + ':' + parameter + ':' + entryParameter;
  const context = useRef({ identity, farm, revision: 0 });
  if (context.current.identity !== identity) context.current = { identity, farm, revision: 0 };
  else if (farm != null) {
    if (context.current.farm != null && context.current.farm !== farm) context.current.revision++;
    context.current.farm = farm;
  }
  return (
    <EntregaForm
      key={identity + ':' + context.current.revision}
      parameter={parameter}
      entryParameter={entryParameter}
    />
  );
}
function EntregaForm({
  parameter,
  entryParameter,
}: {
  parameter: string | undefined;
  entryParameter: string | null;
}) {
  const [search] = useSearchParams();
  const id = useEntityId(parameter, 'camadas'),
    entryId = useEntityId(entryParameter, 'entregas');
  const edit = entryParameter != null;
  const activeQ = useCamadaActiva(),
    typesQ = useTiposPienso();
  const entriesQ = useEntregasDeCamada(edit ? id : null);
  const create = useCreateEntregaPienso(id ?? 0),
    update = useUpdateEntregaPienso(id ?? 0, entryId ?? 0);
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const owner = user?.id;
  const scope = useRef({ owner, farm: currentBundle()?.explotacion_id ?? null, role: user?.rol });
  if (scope.current.farm == null) scope.current.farm = currentBundle()?.explotacion_id ?? null;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = () =>
    mounted.current &&
    useAuthStore.getState().user?.id === scope.current.owner &&
    useAuthStore.getState().user?.rol === scope.current.role &&
    (scope.current.farm == null || currentBundle()?.explotacion_id === scope.current.farm);
  const camada = activeQ.data;
  const entry = entriesQ.data?.find((item) => item.id === entryId);
  const ready =
    id != null &&
    camada?.id === id &&
    !camada.cerrada &&
    !!typesQ.data &&
    !activeQ.isError &&
    !typesQ.isError &&
    (!edit || (!!entry && !entriesQ.isError));
  const canWrite = user?.rol === 'admin' || user?.rol === 'ganadero';
  const [draft, setDraft] = useState<Draft | null>(null),
    [baseline, setBaseline] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const extras = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!ready || draft) return;
    const initial = {
      fecha: entry?.fecha ?? format(new Date(), 'yyyy-MM-dd'),
      kg: entradaDecimal(entry?.kg ?? null),
      tipo: entry ? String(entry.piensotipo_id) : '',
      albaran: entry?.num_albaran_procavi ?? '',
      observaciones: entry?.observaciones ?? '',
    };
    setDraft(initial);
    setBaseline(initial);
  }, [ready, draft, entry]);
  useEffect(() => {
    if (errors.albaran && extras.current) extras.current.open = true;
  }, [errors.albaran]);
  const dirty =
    !!draft &&
    !!baseline &&
    JSON.stringify(canonical(draft)) !== JSON.stringify(canonical(baseline));
  const from = search.get('from'),
    back = search.get('back');
  const destination =
    from === 'pienso'
      ? '/pienso'
      : from === 'listado'
        ? '/camadas/' + id + '/entregas-pienso' + (back === 'pienso' ? '?from=pienso' : '')
        : edit
          ? '/camadas/' + id
          : '/pienso';
  const backLabel =
    from === 'listado' ? 'Entregas' : from === 'pienso' || !edit ? 'Pienso' : 'Camada';
  function change(field: keyof Draft, value: string) {
    setDraft((old) => old && { ...old, [field]: value });
    setErrors((old) => {
      const next = { ...old };
      delete next[field];
      return next;
    });
  }
  async function save() {
    if (!ready || !canWrite || !draft || !current()) return;
    if (edit && !dirty) return destination;
    const found: Record<string, string> = {};
    const date = parseISO(draft.fecha);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.fecha) ||
      !isValid(date) ||
      format(date, 'yyyy-MM-dd') !== draft.fecha ||
      draft.fecha < camada.fecha_entrada
    )
      found.fecha = 'Selecciona una fecha desde la entrada de la camada.';
    let kg = '';
    try {
      const value = new Decimal(decimalSolicitud(draft.kg));
      if (value.compare('0.01') < 0 || value.compare('99999999.99') > 0) throw new Error();
      kg = value.toString();
    } catch {
      found.kg = 'Introduce entre 0,01 y 99.999.999,99 kg.';
    }
    const tipo = Number(draft.tipo);
    if (!Number.isInteger(tipo) || tipo <= 0 || !typesQ.data?.some((item) => item.id === tipo))
      found.tipo = 'Selecciona un tipo de pienso disponible.';
    if (Array.from(draft.albaran.trim()).length > 100)
      found.albaran = 'El albarán admite hasta 100 caracteres.';
    setErrors(found);
    if (Object.keys(found).length) throw new Error('Revisa los campos indicados antes de guardar.');
    const values = {
      fecha: draft.fecha,
      kg,
      piensotipo_id: tipo,
      num_albaran_procavi: draft.albaran.trim() || null,
      observaciones: draft.observaciones.trim() || null,
    };
    if (edit) await update.mutateAsync(values);
    else await create.mutateAsync(values);
    if (!current()) return;
    toast.show({
      kind: 'success',
      message: 'Entrega guardada.',
    });
    return destination;
  }
  const loading = activeQ.isLoading || typesQ.isLoading || (edit && entriesQ.isLoading);
  const unavailable = activeQ.isError || typesQ.isError || (edit && entriesQ.isError);
  function field(name: 'fecha' | 'kg' | 'albaran', label: string) {
    return (
      <div className="cm-form-field">
        <label htmlFor={'feed-' + name}>{label}</label>
        <input
          id={'feed-' + name}
          className="ui-field"
          value={draft?.[name] ?? ''}
          type={name === 'fecha' ? 'date' : 'text'}
          inputMode={name === 'kg' ? 'decimal' : undefined}
          min={name === 'fecha' ? camada?.fecha_entrada : undefined}
          autoComplete="off"
          maxLength={name === 'albaran' ? 100 : undefined}
          onChange={(e) => change(name, e.target.value)}
          aria-invalid={!!errors[name]}
          aria-describedby={errors[name] ? 'feed-' + name + '-error' : undefined}
        />
        {errors[name] && (
          <p id={'feed-' + name + '-error'} className="text-warning-text">
            {errors[name]}
          </p>
        )}
      </div>
    );
  }
  let summary = '—';
  try {
    const value = new Decimal(decimalSolicitud(draft?.kg ?? ''));
    if (value.compare(0) > 0) summary = formatoDecimal(value.toString(), 0) + ' kg';
  } catch {
    /* Empty or invalid input has no preview. */
  }
  return (
    <MobileFormPage
      title={edit ? 'Editar entrega de pienso' : 'Entrega de pienso'}
      backTo={destination}
      backLabel={backLabel}
      dirty={dirty}
      disabled={!ready || !draft || !canWrite}
      saveLabel={edit ? (dirty ? 'Guardar cambios' : 'Terminar') : 'Guardar entrega'}
      onSave={save}
    >
      {camada?.id === id && (
        <div className="cm-form-context">
          <span>{camada.codigo_lote}</span>
          <span>Pienso entregado</span>
        </div>
      )}
      {loading ? (
        <p className="cm-form-note">Cargando entrega…</p>
      ) : unavailable ? (
        <p role="alert" className="parte-error">
          {extractApiMessage(
            activeQ.error ?? typesQ.error ?? entriesQ.error,
            'No se pudo cargar la entrega o el catálogo.',
          )}
        </p>
      ) : !ready ? (
        <p role="alert" className="parte-error">
          Esta entrega no está disponible para editar. Sólo puedes registrar pienso en la camada
          activa.
        </p>
      ) : null}
      {!canWrite && (
        <p role="alert" className="cm-form-note">
          Tu usuario no tiene permiso para modificar entregas.
        </p>
      )}
      {draft && (
        <fieldset disabled={!ready || !canWrite} className="cm-form-fields">
          {field('fecha', 'Fecha de entrega')}
          {field('kg', 'Cantidad entregada (kg)')}
          <div className="cm-form-field">
            <label htmlFor="feed-tipo">Tipo de pienso</label>
            <select
              id="feed-tipo"
              className="ui-field"
              value={draft.tipo}
              onChange={(e) => change('tipo', e.target.value)}
              aria-invalid={!!errors.tipo}
              aria-describedby={errors.tipo ? 'feed-tipo-error' : undefined}
            >
              <option value="">Selecciona un tipo</option>
              {typesQ.data?.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.codigo}
                  {type.descripcion ? ' · ' + type.descripcion : ''}
                </option>
              ))}
            </select>
            {errors.tipo && (
              <p id="feed-tipo-error" className="text-warning-text">
                {errors.tipo}
              </p>
            )}
          </div>
          <details ref={extras} className="border-y border-border">
            <summary className="min-h-12 cursor-pointer py-3 text-sm">
              Albarán y observaciones · Opcional
            </summary>
            <div className="cm-form-fields pb-4">
              {field('albaran', 'Albarán Procavi')}
              <div className="cm-form-field">
                <label htmlFor="feed-observaciones">Observaciones</label>
                <textarea
                  id="feed-observaciones"
                  className="ui-field"
                  rows={3}
                  value={draft.observaciones}
                  onChange={(e) => change('observaciones', e.target.value)}
                />
              </div>
            </div>
          </details>
          <div className="cm-arrival-totals" aria-live="polite">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm">Entrega</span>
              <strong className="text-xl font-semibold tabular-nums">{summary}</strong>
            </div>
            <p className="cm-form-note">
              {typesQ.data?.find((type) => String(type.id) === draft.tipo)?.codigo ??
                'Selecciona el tipo de pienso.'}
            </p>
          </div>
        </fieldset>
      )}
    </MobileFormPage>
  );
}
export default CargaEntregaPiensoPage;
